import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShoppingCart } from "lucide-react";
import { api, money, dateTime } from "@/lib/api";
import { Card, PageHeader, StatusBadge, EmptyState, inputCls } from "@/components/admin/ui";

const TABS = [
  { id: "paid", label: "All paid", params: { payment: "paid" } },
  { id: "to_ship", label: "To ship", params: { payment: "paid", fulfillment: "unfulfilled" } },
  { id: "shipped", label: "Shipped", params: { fulfillment: "shipped" } },
  { id: "delivered", label: "Delivered", params: { fulfillment: "delivered" } },
  { id: "pending", label: "Unpaid checkouts", params: { payment: "pending" } },
  { id: "cancelled", label: "Cancelled", params: { fulfillment: "cancelled" } },
];

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "paid";
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState(null);
  const nav = useNavigate();
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    api.post("/admin/orders/reconcile").finally(() => setSynced(true));
  }, []);

  useEffect(() => {
    if (!synced) return undefined;
    const t = TABS.find((x) => x.id === tab) || TABS[0];
    const timer = setTimeout(() => {
      api.get("/admin/orders", { params: { ...t.params, q: q || undefined } }).then((r) => setOrders(r.data));
    }, 200);
    return () => clearTimeout(timer);
  }, [tab, q, synced]);

  return (
    <div data-testid="admin-orders-page">
      <PageHeader title="Orders" subtitle="Manage payments, shipping and tracking." />
      <Card className="p-0 sm:p-0 overflow-hidden">
        <div className="flex gap-1 px-3 pt-3 overflow-x-auto border-b border-zinc-200">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setParams({ tab: t.id })} data-testid={`orders-tab-${t.id}`}
              className={`whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-3 border-b border-zinc-200">
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order #, name or email" className={`${inputCls} pl-9`} data-testid="orders-search-input" />
          </div>
        </div>
        {orders && orders.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="No orders here yet" text="When customers buy from your store, their orders show up here." testid="orders-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-zinc-50 text-zinc-500 text-xs">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Order</th>
                  <th className="text-left font-medium px-4 py-2.5">Date</th>
                  <th className="text-left font-medium px-4 py-2.5">Customer</th>
                  <th className="text-left font-medium px-4 py-2.5">Payment</th>
                  <th className="text-left font-medium px-4 py-2.5">Shipping</th>
                  <th className="text-right font-medium px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(orders || []).map((o) => (
                  <tr key={o.id} onClick={() => nav(`/admin/orders/${o.id}`)} className="hover:bg-zinc-50 cursor-pointer" data-testid={`order-row-${o.number}`}>
                    <td className="px-4 py-3 font-semibold"><Link to={`/admin/orders/${o.id}`}>#{o.number}</Link></td>
                    <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{dateTime(o.created_at)}</td>
                    <td className="px-4 py-3">{o.customer.name}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.payment_status} /></td>
                    <td className="px-4 py-3"><StatusBadge status={o.fulfillment_status} /></td>
                    <td className="px-4 py-3 text-right">{money(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
